import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/use-toast';
import { tasksMutationLimiter, checkRateLimit } from '@/lib/rateLimiter';
import { awardPoints } from '@/lib/gamification';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type Task = Tables<'tasks'>;
export type TaskInsert = TablesInsert<'tasks'>;
export type TaskUpdate = TablesUpdate<'tasks'>;

export type TaskCategory = 'work' | 'personal' | 'upskilling' | 'other';
export type TaskPriority = 'high' | 'medium' | 'low';
export type TaskStatus = 'todo' | 'in_progress' | 'done' | 'archived';

export interface TaskFilters {
  category?: TaskCategory | 'all';
  status?: TaskStatus | 'all';
  priority?: TaskPriority | 'all';
  timeRange?: 'all' | 'today' | 'this_week' | 'overdue' | 'no_due_date';
  search?: string;
}

export function useTasks(filters?: TaskFilters) {
  const user = useAuthStore((s) => s.user);

  return useQuery({
    queryKey: ['tasks', user?.id, filters],
    queryFn: async () => {
      if (!user) return [];
      let query = supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (filters?.category && filters.category !== 'all') {
        query = query.eq('category', filters.category);
      }
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.priority && filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }
      if (filters?.search) {
        const sanitized = filters.search.replace(/[%_]/g, '');
        query = query.or(`title.ilike.%${sanitized}%,description.ilike.%${sanitized}%`);
      }
      if (filters?.timeRange && filters.timeRange !== 'all') {
        const today = new Date().toISOString().split('T')[0];
        const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
        switch (filters.timeRange) {
          case 'today': query = query.eq('due_date', today); break;
          case 'this_week': query = query.gte('due_date', today).lte('due_date', weekEnd); break;
          case 'overdue': query = query.lt('due_date', today).neq('status', 'done'); break;
          case 'no_due_date': query = query.is('due_date', null); break;
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Task[];
    },
    enabled: !!user,
    retry: 2,
    staleTime: 30_000,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (task: Omit<TaskInsert, 'user_id'>) => {
      checkRateLimit(tasksMutationLimiter, 'createTask');
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('tasks')
        .insert({ ...task, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: 'Task created' });
    },
    onError: (e) => toast({ title: 'Error creating task', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: TaskUpdate & { id: string }) => {
      checkRateLimit(tasksMutationLimiter, 'updateTask');
      const updatedPayload: Record<string, unknown> = { ...updates, updated_at: new Date().toISOString() };
      if (updates.status === 'done' && !updates.completed_at) {
        updatedPayload.completed_at = new Date().toISOString();
      } else if (updates.status && updates.status !== 'done') {
        updatedPayload.completed_at = null;
      }
      const { data, error } = await supabase
        .from('tasks')
        .update(updatedPayload as TaskUpdate)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: 'Task updated' });
      // Award XP for task completion
      if (variables.status === 'done' && user?.id) {
        awardPoints(user.id, 'task_complete').then(() => {
          qc.invalidateQueries({ queryKey: ['gamification'] });
        });
      }
    },
    onError: (e) => toast({ title: 'Error updating task', description: e.message, variant: 'destructive' }),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      checkRateLimit(tasksMutationLimiter, 'deleteTask');
      const { error } = await supabase.from('tasks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast({ title: 'Task deleted' });
    },
    onError: (e) => toast({ title: 'Error deleting task', description: e.message, variant: 'destructive' }),
  });
}
